import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { fetchMatkaSuggestions } from "../../../api/services/matka";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

const POINTS_ERROR = "Points must be between 1 and 10,000.";
const DIGIT_NOTICE =
  "Enter Left Digit and/or Right Digit, then Points. Use suggestions or both digits.";

function uniqueJodis(list) {
  const seen = new Set();
  const next = [];
  list.forEach((jodi) => {
    const value = String(jodi);
    if (!/^\d{2}$/.test(value) || seen.has(value)) return;
    seen.add(value);
    next.push(value);
  });
  return next;
}

export default function MatkaDigitBasedJodi({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [gameType, setGameType] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0].toLowerCase()
  );
  const [leftDigit, setLeftDigit] = useState("");
  const [rightDigit, setRightDigit] = useState("");
  const [enterPoints, setEnterPoints] = useState("");
  const [leftLoading, setLeftLoading] = useState(false);
  const [rightLoading, setRightLoading] = useState(false);
  const [pointsError, setPointsError] = useState("");
  const [notice, setNotice] = useState("");
  const [bids, setBids] = useState([]);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: gameType,
    bids,
    onPlaced: () => setBids([]),
  });
  const leftRequest = useRef(0);
  const rightRequest = useRef(0);
  const leftPending = useRef(null);
  const rightPending = useRef(null);
  const leftSuggestionsRef = useRef([]);
  const rightSuggestionsRef = useRef([]);
  const rightRef = useRef(null);
  const pointsRef = useRef(null);

  const totalBids = bids.length;
  const totalPoints = useMemo(
    () => bids.reduce((sum, bid) => sum + (Number(bid.points) || 0), 0),
    [bids]
  );
  const loading = leftLoading || rightLoading;

  useEffect(
    () => () => {
      leftRequest.current += 1;
      rightRequest.current += 1;
    },
    []
  );

  const clearSide = (side) => {
    if (side === "left") {
      leftRequest.current += 1;
      leftPending.current = null;
      leftSuggestionsRef.current = [];
      setLeftLoading(false);
      return;
    }
    rightRequest.current += 1;
    rightPending.current = null;
    rightSuggestionsRef.current = [];
    setRightLoading(false);
  };

  const loadSide = (side, digit) => {
    const pattern = side === "left" ? `${digit}x` : `x${digit}`;
    const requestRef = side === "left" ? leftRequest : rightRequest;
    const id = requestRef.current + 1;
    requestRef.current = id;
    if (side === "left") setLeftLoading(true);
    else setRightLoading(true);

    const promise = fetchMatkaSuggestions("digits_based_jodi", pattern)
      .then((data) => {
        if (id !== requestRef.current) return null;
        const jodis = uniqueJodis(Array.isArray(data?.jodis) ? data.jodis : []);
        if (side === "left") leftSuggestionsRef.current = jodis;
        else rightSuggestionsRef.current = jodis;
        return jodis;
      })
      .catch(() => {
        if (id !== requestRef.current) return null;
        if (side === "left") leftSuggestionsRef.current = [];
        else rightSuggestionsRef.current = [];
        return [];
      })
      .finally(() => {
        if (id !== requestRef.current) return;
        if (side === "left") setLeftLoading(false);
        else setRightLoading(false);
      });

    if (side === "left") leftPending.current = promise;
    else rightPending.current = promise;
  };

  const handleLeftDigitChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 1);
    setLeftDigit(next);
    clearSide("left");
    if (!next) return;
    rightRef.current?.focus();
    loadSide("left", next);
  };

  const handleRightDigitChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 1);
    setRightDigit(next);
    clearSide("right");
    if (!next) return;
    pointsRef.current?.focus();
    loadSide("right", next);
  };

  const handlePointsChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 5);
    setEnterPoints(next);
    if (!next) {
      setPointsError("");
      return;
    }
    const value = parseInt(next, 10);
    setPointsError(value >= 1 && value <= 10000 ? "" : POINTS_ERROR);
  };

  const awaitSide = async (pendingRef, suggestionsRef) => {
    while (pendingRef.current) {
      const pending = pendingRef.current;
      await pending;
      if (pendingRef.current === pending || pendingRef.current === null) break;
    }
    return suggestionsRef.current;
  };

  const handleAddBid = async (event) => {
    if (event && event.preventDefault) event.preventDefault();

    if (enterPoints.trim() === "") {
      setNotice("Please enter points");
      return;
    }
    const pointsValue = parseInt(enterPoints, 10);
    if (!Number.isInteger(pointsValue) || pointsValue < 1 || pointsValue > 10000) {
      setPointsError(POINTS_ERROR);
      return;
    }

    const leftList = leftDigit ? await awaitSide(leftPending, leftSuggestionsRef) : [];
    const rightList = rightDigit ? await awaitSide(rightPending, rightSuggestionsRef) : [];

    let jodis = [];
    if (leftDigit && rightDigit) {
      jodis =
        !leftList.length && !rightList.length
          ? [`${leftDigit}${rightDigit}`]
          : uniqueJodis([...leftList, ...rightList]);
    } else if (leftDigit && leftList.length) {
      jodis = leftList;
    } else if (rightDigit && rightList.length) {
      jodis = rightList;
    } else {
      setNotice(DIGIT_NOTICE);
      return;
    }

    setBids((currentBids) => {
      const nextBids = [...currentBids];
      jodis.forEach((jodi, index) => {
        const existingIndex = nextBids.findIndex(
          (bid) => bid.jodi === jodi && bid.gameType === gameType
        );
        if (existingIndex >= 0) {
          nextBids[existingIndex] = {
            ...nextBids[existingIndex],
            points: Number(nextBids[existingIndex].points) + pointsValue,
          };
          return;
        }
        nextBids.push({
          id: Date.now() + index,
          jodi,
          points: pointsValue,
          gameType,
        });
      });
      return nextBids;
    });

    if (leftDigit) {
      setLeftDigit("");
      clearSide("left");
    }
    if (rightDigit) {
      setRightDigit("");
      clearSide("right");
    }
    setEnterPoints("");
    setPointsError("");
  };

  const handleGameTypeChange = (nextGameType) => {
    setGameType(nextGameType);
    setBids((currentBids) =>
      applySessionToBids(currentBids, nextGameType, (bid) => bid.jodi)
    );
  };

  const handleDeleteBid = (bidId) => {
    setBids((currentBids) => currentBids.filter((bid) => bid.id !== bidId));
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Header */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="matka-betting-body pt-3 pb-4">
        <div className="matka-main-centered-container">
          {/* Back to Market Link */}
          <button
            type="button"
            className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
            onClick={onBackToGameTypes}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Back to{" "}
            {selectedMarket?.name
              ? selectedMarket.name.charAt(0) +
                selectedMarket.name.slice(1).toLowerCase()
              : "Mahadevi"}
          </button>

          {/* Screen Title */}
          <h1 className="matka-panna-bulk-title mb-4">Digit Based Jodi</h1>
          <div className="mb-4">
            <label className="matka-panna-bulk-label">GAME TYPE</label>
            <MatkaSessionSelect
              betDirection={selectedGameType?.betDirection}
              variant="bulk"
              value={gameType}
              onChange={handleGameTypeChange}
            />
          </div>

          {/* Form: Row 1 - LEFT DIGIT and RIGHT DIGIT */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">LEFT DIGIT</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="matka-panna-bulk-input"
                placeholder="1"
                value={leftDigit}
                onChange={handleLeftDigitChange}
              />
            </div>

            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">RIGHT DIGIT</label>
              <input
                ref={rightRef}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="matka-panna-bulk-input"
                placeholder="2"
                value={rightDigit}
                onChange={handleRightDigitChange}
              />
            </div>
          </div>

          {/* Form: Row 2 - ENTER POINTS (Full width) */}
          <div className="mb-3">
            <label className="matka-panna-bulk-label">ENTER POINTS</label>
            <input
              ref={pointsRef}
              type="text"
              inputMode="numeric"
              className="matka-panna-bulk-input"
              placeholder="Enter Points (e.g. 100)"
              value={enterPoints}
              onChange={handlePointsChange}
            />
            {pointsError && <p className="matka-sangam-error">{pointsError}</p>}
          </div>

          {/* Add Bid Button (Right-Aligned) */}
          <div className="d-flex justify-content-end mb-4">
            <button
              type="button"
              className="matka-add-bid-btn"
              onClick={handleAddBid}
              disabled={loading}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-label="Loading"
                ></span>
              ) : (
                "ADD BID"
              )}
            </button>
          </div>

          {/* Current Bids List Card */}
          <div className="matka-bids-list-card mb-4">
            <h3 className="matka-bids-list-title">Current Bids List</h3>
            <div className="table-responsive">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th className="th-panna">Jodi</th>
                    <th className="th-type text-center">Game Type</th>
                    <th className="th-points text-end">Points (₹)</th>
                    <th className="th-action" aria-label="Delete"></th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((bid) => (
                    <tr key={bid.id}>
                      <td className="td-panna">{bid.jodi}</td>
                      <td className="td-type text-center">{bid.gameType}</td>
                      <td className="td-points text-end">{bid.points}</td>
                      <td className="td-action text-end">
                        <button
                          type="button"
                          className="matka-jodi-delete-btn"
                          onClick={() => handleDeleteBid(bid.id)}
                          aria-label={`Delete ${bid.jodi}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* In-page Action Bar with Bids, Points, and CONTINUE */}
          <div className="matka-bulk-summary-bar">
            <div className="d-flex align-items-center gap-3">
              {/* Bids Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">{totalBids}</span>
                <span className="matka-bulk-counter-label">Bids</span>
              </div>

              {/* Total Points Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="matka-bulk-counter-label">Total Points</span>
              </div>
            </div>

            {/* Continue Action Button */}
            <button
              type="button"
              className="matka-bulk-continue-btn"
              onClick={handleContinue}
              disabled={totalBids === 0}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </main>

      {notice && (
        <div className="matka-sangam-popup" role="dialog" aria-modal="true">
          <div className="matka-sangam-popup-box">
            <p>{notice}</p>
            <button type="button" onClick={() => setNotice("")}>
              OK
            </button>
          </div>
        </div>
      )}

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
